import React, { useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { Target, TrendingUp, Zap, Calendar, Users, BarChart3, Clock, Lightbulb } from 'lucide-react';


const TestimonialSection = () => {
  const navigate = useNavigate();

  const achievements = {
    column1: [
      {
        icon: Target,
        title: "30-Day Strategy Builder",
        metric: "SMART Goals & KPIs",
        description: "AI builds your complete 30-day marketing strategy with brand positioning, channel mix, and measurable goals in 15 minutes.",
        color: "blue"
      },
      {
        icon: Calendar,
        title: "30-Day Content Calendar",
        metric: "Every Day Planned",
        description: "Every day has a post brief, AI caption, hashtags, and platform assigned — so you always know exactly what to create.",
        color: "green"
      },
      {
        icon: Lightbulb,
        title: "AI Captions & Hashtags",
        metric: "Every Post",
        description: "Platform-optimised captions and hashtag sets generated for every post in your calendar — ready to review and publish.",
        color: "purple"
      },
      {
        icon: BarChart3,
        title: "Campaign PDF Report",
        metric: "Downloadable",
        description: "Download a professional PDF summary of your campaign progress, posting streak, and results at any time.",
        color: "blue"
      }
    ],
    column2: [
      {
        icon: Zap,
        title: "Design Studio",
        metric: "12 Templates",
        description: "Canva-style editor with 12 layout templates. Drag, resize, and customise graphics without needing a designer.",
        color: "green"
      },
      {
        icon: TrendingUp,
        title: "AI Image Generation",
        metric: "6 Styles",
        description: "Generate on-brand background images in 6 visual styles and 4 colour harmonies — no stock photos needed.",
        color: "purple"
      },
      {
        icon: Target,
        title: "Logo Background Removal",
        metric: "One Click",
        description: "Remove white or light backgrounds from your logo instantly so it sits cleanly on any design template.",
        color: "blue"
      },
      {
        icon: Lightbulb,
        title: "14 Text Styles",
        metric: "Google Fonts",
        description: "Choose from 14 professional text styles — from bold headlines to elegant scripts — all powered by Google Fonts.",
        color: "green"
      }
    ],
    column3: [
      {
        icon: Zap,
        title: "Direct Instagram Publishing",
        metric: "One-Click Post",
        description: "Connect your Instagram account and publish your designed posts directly from the platform — no third-party tools needed.",
        color: "purple"
      },
      {
        icon: Users,
        title: "Direct Facebook Publishing",
        metric: "One-Click Post",
        description: "Connect your Facebook Page and publish content straight from your campaign calendar to your audience.",
        color: "blue"
      },
      {
        icon: Clock,
        title: "Guided Video Workflow",
        metric: "4-Step Process",
        description: "Brief → Script → Film → Edit. AI writes your scene-by-scene script and guides you through filming and editing every video.",
        color: "green"
      },
      {
        icon: TrendingUp,
        title: "Posting Streak Tracking",
        metric: "Stay Consistent",
        description: "Track your campaign completion rate and posting streak so you can see your consistency and momentum at a glance.",
        color: "purple"
      }
    ]
  };

  return (
    <div className="bg-gradient-to-b from-slate-50 to-white py-16 md:py-20 px-4">
      {/* Main Heading */}
      <div className="text-center max-w-4xl mx-auto mb-12 md:mb-16">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-[1.08] mb-3">
          What you'll<br />
          <span style={{ backgroundImage: 'linear-gradient(90deg,#60a5fa 0%,#34d399 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            achieve.
          </span>
        </h2>
        <p className="text-sm md:text-base text-gray-600 mt-4">
          Powerful features designed to transform your marketing and drive real business growth
        </p>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">

          {/* Left Side - Heading and CTA */}
          <div className="space-y-6 md:space-y-8 lg:pr-12">
            <div className="space-y-4 md:space-y-6">
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Everything You Need to Plan, Create & Publish
              </h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                From AI strategy to Design Studio to direct social publishing — everything your 30-day marketing campaign needs is in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-medium">No credit card required</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-medium">14-day free trial</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate("/auth/signup")}
              className="bg-blue-600 text-white px-8 md:px-10 py-4 rounded-lg text-base md:text-lg font-semibold hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center gap-2 group"
            >
              <span>Start Your Free Trial</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>

          </div>

          {/* Right Side - Three Columns of Auto-scrolling Cards */}
          <div className="relative h-[600px] overflow-hidden">
            <div className="flex gap-4 h-full">
              {/* Column 1 */}
              <ScrollingColumn achievements={achievements.column1} speed={30} />

              {/* Column 2 */}
              <ScrollingColumn achievements={achievements.column2} speed={40} direction="up" />

              {/* Column 3 */}
              <ScrollingColumn achievements={achievements.column3} speed={35} />
            </div>
            
            {/* Fade overlays */}
            <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-white to-transparent pointer-events-none z-10"></div>
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none z-10"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ScrollingColumn = ({ achievements, speed = 30, direction = 'down' }) => {
  const columnRef = useRef(null);

  useEffect(() => {
    const column = columnRef.current;
    if (!column) return;

    let scrollPosition = direction === 'up' ? column.scrollHeight / 2 : 0;

    const scroll = () => {
      if (direction === 'down') {
        scrollPosition += 0.5;
        if (scrollPosition >= column.scrollHeight / 2) {
          scrollPosition = 0;
        }
      } else {
        scrollPosition -= 0.5;
        if (scrollPosition <= 0) {
          scrollPosition = column.scrollHeight / 2;
        }
      }
      column.scrollTop = scrollPosition;
    };

    const interval = setInterval(scroll, speed);

    return () => clearInterval(interval);
  }, [speed, direction]);

  return (
    <div
      ref={columnRef}
      className="flex-1 overflow-hidden"
      style={{ scrollBehavior: 'auto' }}
    >
      <div className="space-y-4">
        {/* Duplicate achievements for seamless loop */}
        {[...achievements, ...achievements].map((achievement, index) => (
          <AchievementCard key={index} achievement={achievement} />
        ))}
      </div>
    </div>
  );
};

const AchievementCard = ({ achievement }) => {
  const Icon = achievement.icon;

  const colorClasses = {
    blue: {
      bg: 'bg-blue-600',
      border: 'border-blue-200',
      iconBg: 'bg-blue-50',
      text: 'text-blue-600',
      badge: 'bg-blue-50 text-blue-600 border-blue-100'
    },
    green: {
      bg: 'bg-green-600',
      border: 'border-green-200',
      iconBg: 'bg-green-50',
      text: 'text-green-600',
      badge: 'bg-green-50 text-green-600 border-green-100'
    },
    purple: {
      bg: 'bg-purple-600',
      border: 'border-purple-200',
      iconBg: 'bg-purple-50',
      text: 'text-purple-600',
      badge: 'bg-purple-50 text-purple-600 border-purple-100'
    }
  };

  const colors = colorClasses[achievement.color];

  return (
    <div className={`bg-white border-2 ${colors.border} rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${colors.iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${colors.text}`} strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm text-gray-900 leading-tight mb-1">{achievement.title}</h4>
          <span className={`inline-block ${colors.badge} px-2 py-0.5 rounded text-xs font-bold border`}>
            {achievement.metric}
          </span>
        </div>
      </div>

      <p className="text-gray-600 text-xs leading-relaxed">
        {achievement.description}
      </p>
    </div>
  );
};

export default TestimonialSection;